import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import Modal from '../ui/Modal';
import { updateEmployee } from '../../api/employees';
import { getActiveTools } from '../../api/tools';
import { migrateEmployee } from '../../api/migrations';
import { ArrowRightLeft } from 'lucide-react';

const EditEmployeeModal = ({ isOpen, onClose, onSaved, employee, departments }) => {
  const [form, setForm] = useState({ name: '', email: '', department: '', monthly_cost: '', paid_by: 'Bacancy' });
  const [tools, setTools] = useState([]);
  const [selectedToolId, setSelectedToolId] = useState('');
  const [migrationDate, setMigrationDate] = useState('');
  const [migrationEndDate, setMigrationEndDate] = useState('');
  const [migrationNotes, setMigrationNotes] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const toolChanged = employee && selectedToolId &&
    Number(selectedToolId) !== Number(employee.tool_id);

  useEffect(() => {
    if (isOpen && employee) {
      setForm({
        name: employee.name,
        email: employee.email,
        department: employee.department,
        monthly_cost: employee.monthly_cost != null ? String(employee.monthly_cost) : '',
        paid_by: employee.paid_by || 'Bacancy',
      });
      setSelectedToolId(employee.tool_id ? String(employee.tool_id) : '');
      setMigrationDate('');
      setMigrationNotes('');
      setMigrationEndDate('');
      setErrors({});
      getActiveTools()
        .then((res) => setTools(res.data))
        .catch(() => toast.error('Could not load tools.'));
    }
  }, [isOpen, employee]);

  // Reset migration end date when start date is cleared
  useEffect(() => {
    if (!migrationDate) setMigrationEndDate('');
  }, [migrationDate]);

  // When tool changes, auto-fill monthly_cost from new tool's price
  useEffect(() => {
    if (!isOpen || !employee) return;
    if (toolChanged && selectedToolId) {
      const tool = tools.find((t) => String(t.id) === String(selectedToolId));
      if (tool) {
        setForm((prev) => ({ ...prev, monthly_cost: Number(tool.monthly_price).toFixed(2) }));
      }
    } else if (!toolChanged && employee) {
      // Revert to original allocation cost when tool is reset
      setForm((prev) => ({
        ...prev,
        monthly_cost: employee.monthly_cost != null ? String(employee.monthly_cost) : '',
      }));
    }
  }, [selectedToolId, tools, toolChanged, isOpen, employee]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required.';
    if (!form.email.trim()) errs.email = 'Email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email.';
    if (!form.department) errs.department = 'Department is required.';
    if (toolChanged && !migrationDate) errs.migrationDate = 'Start date is required to change tool.';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    try {
      // 1. Update basic employee info + allocation fields
      await updateEmployee(employee.id, {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        department: form.department,
        monthly_cost: form.monthly_cost ? Number(form.monthly_cost) : undefined,
        paid_by: form.paid_by || 'Bacancy',
      });

      // 2. If tool changed, trigger migration
      if (toolChanged) {
        await migrateEmployee({
          employee_id: employee.id,
          ai_tool_id: Number(selectedToolId),
          start_date: migrationDate,
          end_date: migrationEndDate || undefined,
          notes: migrationNotes.trim() || undefined,
          monthly_cost: form.monthly_cost ? Number(form.monthly_cost) : undefined,
          paid_by: form.paid_by || 'Bacancy',
        });
        toast.success('Employee updated and tool changed successfully.');
      } else {
        toast.success('Employee updated.');
      }

      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update employee.');
    } finally {
      setLoading(false);
    }
  };

  const currentToolName = employee?.tool_name || 'No tool assigned';
  const newToolName = tools.find((t) => t.id === Number(selectedToolId))?.name;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Employee" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Employee Info */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Employee Information</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Full Name</label>
              <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} />
              {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="label">Email Address</label>
              <input className="input" type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>
            <div className="sm:col-span-2">
              <label className="label">Department</label>
              <select className="input" value={form.department} onChange={(e) => set('department', e.target.value)}>
                <option value="">Select department...</option>
                {departments.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              {errors.department && <p className="text-xs text-red-500 mt-1">{errors.department}</p>}
            </div>
            <div>
              <label className="label">Monthly Charge (USD) <span className="text-gray-400 font-normal">(from tool price)</span></label>
              <input
                className="input"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={form.monthly_cost}
                onChange={(e) => set('monthly_cost', e.target.value)}
              />
            </div>
            <div>
              <label className="label">Paid By</label>
              <select className="input" value={form.paid_by} onChange={(e) => set('paid_by', e.target.value)}>
                <option value="Bacancy">Bacancy</option>
                <option value="Client">Client</option>
              </select>
            </div>
          </div>
        </div>

        <hr className="border-gray-100" />

        {/* AI Tool Section */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">AI Tool</p>

          <div>
            <label className="label">Assigned AI Tool</label>
            <select
              className="input"
              value={selectedToolId}
              onChange={(e) => setSelectedToolId(e.target.value)}
            >
              <option value="">No tool assigned</option>
              {tools.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} — ${Number(t.monthly_price).toFixed(2)}/mo
                </option>
              ))}
            </select>
          </div>

          {/* Show migration fields only when tool is changed */}
          {toolChanged && (
            <div className="mt-4 space-y-3">
              {/* From → To visual */}
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-red-50 border border-red-100 rounded-xl px-3 py-2 text-center">
                  <p className="text-xs text-gray-400 mb-0.5">Current</p>
                  <p className="text-sm font-semibold text-red-700 truncate">{currentToolName}</p>
                </div>
                <ArrowRightLeft className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <div className="flex-1 bg-green-50 border border-green-100 rounded-xl px-3 py-2 text-center">
                  <p className="text-xs text-gray-400 mb-0.5">New</p>
                  <p className="text-sm font-semibold text-green-700 truncate">{newToolName}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">Effective Start Date</label>
                  <input
                    className="input"
                    type="date"
                    value={migrationDate}
                    onChange={(e) => setMigrationDate(e.target.value)}
                  />
                  {errors.migrationDate && <p className="text-xs text-red-500 mt-1">{errors.migrationDate}</p>}
                </div>
                <div>
                  <label className="label">End Date <span className="text-gray-400 font-normal">(optional)</span></label>
                  <input
                    className="input"
                    type="date"
                    value={migrationEndDate}
                    min={migrationDate || undefined}
                    onChange={(e) => setMigrationEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="label">Notes <span className="text-gray-400 font-normal">(optional)</span></label>
                <input
                  className="input"
                  placeholder="Reason for tool change..."
                  value={migrationNotes}
                  onChange={(e) => setMigrationNotes(e.target.value)}
                />
              </div>

              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                Changing the tool will record a migration and expire the current allocation.
              </p>
            </div>
          )}
        </div>

        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button type="submit" className="btn-primary flex-1" disabled={loading}>
            {loading ? 'Saving...' : toolChanged ? 'Save & Change Tool' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditEmployeeModal;
